import CONFIG from '../config/config';
import { instanceWithV2 as instance } from './SimpleAPIClient';

export type GownType = 'ballgown' | 'mermaid' | 'a-line' | 'sheath' | 'trumpet' | 'tea-length' | 'other';
export type GownStyle = 'classic' | 'modern' | 'vintage' | 'boho' | 'romantic' | 'minimalist';
export type GownColor = 'white' | 'ivory' | 'blush' | 'champagne' | 'silver' | 'gold' | 'other';

export interface DressFeatures {
  fitAndFlare?: boolean;
  sweetheart?: boolean;
  lowBack?: boolean;
  strapless?: boolean;
  floorLength?: boolean;
}

export interface Condition {
  isNew?: boolean;
  hasFlaws?: boolean;
  dryCleaned?: boolean;
  fabricType?: string;
  hasBeenAltered?: boolean;
}

export interface ReturnsPolicy {
  misrepresented?: boolean;
  counterfeit?: boolean;
  doesntFit?: boolean;
  damaged?: boolean;
  neverArrives?: boolean;
  changeOfMind?: boolean;
}

export interface StoreOffer {
  id?: number;
  name: string;
  description?: string;
  price: number;
  currency: string;
  images: string[];
  featuredImage?: string;
  gownType: GownType;
  gownStyle: GownStyle;
  gownColor: GownColor;
  size?: string;
  available: boolean;
  quantity?: number;
  featured: boolean;
  onSale: boolean;
  salePrice?: number;
  // New fields
  dressFeatures?: DressFeatures;
  specialFeatures?: string[]; // e.g., ["Detachable sleeves"]
  condition?: Condition;
  returnsPolicy?: ReturnsPolicy;
  labelSize?: string; // e.g., "EU 38"
  year?: number; // e.g., 2023
  sellerName?: string;
  sellerType?: 'private' | 'store';
  sellerLocation?: string;
  itemId?: number; // Link to collection item
  createdAt?: string;
  updatedAt?: string;
}

type StoreOfferResponse = StoreOffer;
type StoreOffersResponse = StoreOffer[];

const StoreOffersAPI = {
  getAll: () => {
    return instance(CONFIG.API_ENDPOINT).get<StoreOffersResponse>('v1/store-offers');
  },
  getById: (id: number) => {
    return instance(CONFIG.API_ENDPOINT).get<StoreOfferResponse>(`v1/store-offers/${id}`);
  },
  create: (offer: StoreOffer) => {
    return instance(CONFIG.API_ENDPOINT).post<StoreOfferResponse>('v1/store-offers', offer);
  },
  update: (id: number, offer: StoreOffer) => {
    return instance(CONFIG.API_ENDPOINT).put<StoreOfferResponse>(`v1/store-offers/${id}`, offer);
  },
  delete: (id: number) => {
    return instance(CONFIG.API_ENDPOINT).delete<void>(`v1/store-offers/${id}`);
  },
  setStoreLink: (alias: string) => {
    return instance(CONFIG.API_ENDPOINT).post<string>('v1/store-offers/alias', { alias });
  },
  checkAliasAvailability: (alias: string, signal?: AbortSignal) => {
    return instance(CONFIG.API_ENDPOINT).post<string>('v1/store-offers/alias/check', { alias }, { signal });
  },
  getEnabledStatus: () => {
    return instance(CONFIG.API_ENDPOINT).get<{ enabled: boolean }>('v1/store-offers/enabled');
  },
  updateEnabledStatus: (enabled: boolean) => {
    return instance(CONFIG.API_ENDPOINT).put<{ enabled: boolean }>('v1/store-offers/enabled', { enabled });
  },
  getPublicStore: (alias: string) => {
    return instance(CONFIG.API_ENDPOINT).get<{ offers: StoreOffersResponse; enabled: boolean; storeName?: string; headerImageUrl?: string; headerText?: string; showBookAppointmentButton?: boolean }>(`v1/public/store-offers/${alias}`);
  },
  getStoreSettings: () => {
    return instance(CONFIG.API_ENDPOINT).get<{ headerImageUrl?: string; headerText?: string; showBookAppointmentButton?: boolean }>('v1/store-offers/settings');
  },
  updateStoreSettings: (settings: { headerImageUrl?: string; headerText?: string; showBookAppointmentButton?: boolean }) => {
    return instance(CONFIG.API_ENDPOINT).put<{ headerImageUrl?: string; headerText?: string; showBookAppointmentButton?: boolean }>('v1/store-offers/settings', settings);
  },
};

export default StoreOffersAPI;

